import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, ExternalLink, Folder, AlertCircle, Eye, IndianRupee, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_ORDER = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatMoney = (v, currency = 'INR') => {
  const num = Number(v);
  if (Number.isNaN(num)) return '—';
  const symbol = currency === 'INR' ? '₹' : `${currency} `;
  return `${symbol}${num.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const PayslipPage = ({ user, setUser }) => {
  const [payslipData, setPayslipData] = useState({ payslip_folder_id: null, stored_payslips: [] });
  const [loading, setLoading] = useState(true);
  const [viewingHtml, setViewingHtml] = useState(null);

  const fetchPayslips = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/payslips`, config);
      setPayslipData(response.data);
    } catch (error) {
      toast.error('Failed to load payslips');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayslips();
  }, [fetchPayslips]);

  const openFolderInDrive = () => {
    if (payslipData.payslip_folder_id) {
      window.open(`https://drive.google.com/drive/folders/${payslipData.payslip_folder_id}`, '_blank');
    }
  };

  const handleDownloadPdf = async (p) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/payslips/${p.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip_${p.month}_${p.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Error responses arrive as Blobs with responseType:'blob' — decode so
      // the real backend detail is shown instead of a generic message.
      let msg = 'Failed to download PDF';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          msg = JSON.parse(text)?.detail || msg;
        } else {
          msg = err.response?.data?.detail || msg;
        }
      } catch { /* keep generic message */ }
      toast.error(msg);
    }
  };

  // Group generated payslips by year → month
  const generatedByYear = useMemo(() => {
    const generated = (payslipData.stored_payslips || []).filter((p) => p.type === 'generated');
    const byYear = {};
    generated.forEach((p) => {
      const yr = p.year || new Date(p.generated_at || p.created_at || Date.now()).getFullYear();
      if (!byYear[yr]) byYear[yr] = [];
      byYear[yr].push(p);
    });
    Object.values(byYear).forEach((list) => {
      list.sort((a, b) => (MONTH_ORDER.indexOf(b.month) - MONTH_ORDER.indexOf(a.month)));
    });
    return byYear;
  }, [payslipData]);

  const hasGenerated = Object.keys(generatedByYear).length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50" data-testid="payslip-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payslips</h1>
          <p className="text-gray-600">View and download your salary payslips</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading payslips...</div>
        ) : (
          <div className="space-y-8">
            {/* Generated Payslips (from Pay Distribution runs) */}
            {hasGenerated && (
              <Card data-testid="generated-payslips-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-emerald-800">
                    <FileText className="w-6 h-6" />
                    Generated Payslips
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {Object.entries(generatedByYear)
                    .sort(([a], [b]) => Number(b) - Number(a))
                    .map(([yr, list]) => (
                      <div key={yr}>
                        <h3 className="font-semibold text-gray-700 mb-3 border-b pb-2">{yr}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {list.map((p) => (
                            <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`generated-payslip-${p.id}`}>
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <div className="font-semibold text-slate-800">{p.month} {p.year}</div>
                                    <div className="text-xs text-slate-500">
                                      Cycle {p.cycle_from} → {p.cycle_to}
                                    </div>
                                  </div>
                                  <FileText className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-xs text-slate-500">Net Pay</span>
                                  <span className="text-sm font-semibold text-emerald-700 tabular-nums flex items-center">
                                    <IndianRupee className="w-3 h-3" />
                                    {formatMoney(p?.totals?.net_pay ?? 0, p.salary_currency).replace('₹', '')}
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full text-xs mb-2"
                                  onClick={() => setViewingHtml({ html: p.inline_html, label: `${p.month} ${p.year}`, id: p.id })}
                                  data-testid={`view-payslip-${p.id}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View Payslip
                                </Button>
                                <Button
                                  size="sm"
                                  className="w-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => handleDownloadPdf(p)}
                                  data-testid={`download-payslip-${p.id}`}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Download PDF
                                </Button>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    ))}
                </CardContent>
              </Card>
            )}

            {/* Google Drive Folder Section */}
            {payslipData.payslip_folder_id ? (
              <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-800">
                    <Folder className="w-6 h-6" />
                    Payslip Folder
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Your payslips are stored in a Google Drive folder. Click below to access all your payslip documents.
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    <Button
                      onClick={openFolderInDrive}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="open-payslip-folder-btn"
                    >
                      <Folder className="w-4 h-4 mr-2" />
                      Open Payslip Folder in Drive
                    </Button>
                    <Button
                      variant="outline"
                      onClick={openFolderInDrive}
                      className="border-green-600 text-green-600 hover:bg-green-50"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View All Payslips
                    </Button>
                  </div>
                  <div className="mt-4 p-3 bg-green-100 rounded-lg">
                    <p className="text-sm text-green-800 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        All your monthly payslips are organized in this folder. Click the button above to view and download them.
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : !hasGenerated ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No payslips available</p>
                  <p className="text-sm mt-2">Your payslips will appear here once they are configured by HR.</p>
                </CardContent>
              </Card>
            ) : null}
          </div>
        )}

        <Dialog open={!!viewingHtml} onOpenChange={(o) => !o && setViewingHtml(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto p-0">
            <DialogHeader className="px-6 pt-4">
              <DialogTitle>Payslip · {viewingHtml?.label}</DialogTitle>
            </DialogHeader>
            {viewingHtml?.html && (
              <iframe
                title="payslip"
                srcDoc={viewingHtml.html}
                className="w-full min-h-[70vh] border-0"
                data-testid="payslip-iframe"
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PayslipPage;
